import { getAppMode } from '@databases/modes';
import {
  createPresenceLayerRenderer,
  presenceMessages,
  presenceState,
} from '@databases/presence';
import { getService } from '@databases/service-container';
import { createProjectionPresenceBinding } from '../layouts/projection/create-projection-presence-binding.ts';
import { type ViewProjection } from '../layouts/projection/create-view-projection.ts';
import { type Recyclable } from './types.ts';

/**
 * Layout-owned placement for remote presence, independent of local selections.
 * The current V2 session supplies the same committed ordering as its renderer.
 */
export type TableV2PresenceDependencies = ViewProjection;

/**
 * Owns remote presence for the active V2 view. Register after highlights so
 * reverse teardown releases layers before their controller. Permission-only
 * selection rebuilds do not replace this owner or its cached placement.
 */
export function initializeTableV2Presence(
  getDependencies: (contentId: string) => TableV2PresenceDependencies | undefined,
): Recyclable {
  let stop: VoidFunction | undefined;
  const dispose = () => {
    const previous = stop;
    stop = undefined;
    previous?.();
  };

  return {
    recycleOn: ['view-id-changed', 'layout-type-changed'],
    setup(contentId) {
      dispose();

      if (getAppMode() === 'MOBILE') {
        return;
      }

      const dependencies = getDependencies(contentId);

      if (!dependencies) {
        return;
      }

      const intl = getService('Intl');
      const renderer = createPresenceLayerRenderer({
        controller: getService('TableHighlightsController'),
        ...createProjectionPresenceBinding(dependencies, {
          subscribe: (listener) => presenceState.subscribe(listener),
          getHumanGroups: () => presenceState.getHumanGroups(),
        }),
        formatOverflow: (numberOfAdditionalParticipants) =>
          intl.formatMessage(presenceMessages['presence.nameplate.more'], {
            numberOfAdditionalParticipants,
          }),
      });

      stop = renderer.stopListening;
      renderer.startListening();
    },
    dispose,
  };
}
